import React from 'react';
import {View, StyleSheet, Platform, FlatList} from 'react-native';
import AccountHistoryList from 'src/components/elements/account/AccountHistoryList';
import Styles from 'src/components/Styles';

import Localized from 'src/constants/AppStrings';
import Settings from 'src/Settings';
import type {HistoryItem as HistoryItemType} from 'src/types/TransactionDetail';
import {NewHistoryItem} from './NewHistoryItem';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import AVText from 'src/components/elements/AVText';
import AppRoutes from 'src/AppRoutes';
import NavActions from 'src/actions/NavActions';
import NetInfo from '@react-native-community/netinfo';
import {getHeaderDescriber} from 'src/components/elements/headerDescriptor/DescriptorType';

type SectionViewProps = {
  title: string;
  data: HistoryItemType[];
  indexNo: string | number;
  testId?: string;
};

const getBGColor = () => {
  if (Platform.OS === 'web') {
    return Styles.white;
  } else if (Settings.isRefiveAnd365Pay()) {
    return Styles.tabBarBackgroundColor;
  } else {
    return Styles.Colors.Canteen.lightGray;
  }
};

const ItemSeparator = () => <View style={styles.separator} />;

const HistorySectionView = (props: SectionViewProps) => {
  const renderItem = ({item, index}) => {
    return (
      <View
        style={[styles.historyContainer]}
        key={`${index} ${item.Id}`}
        accessibilityLabel={item.TransactionTypeDisplay + 'tile'}
        testID={item.Id}
      >
        <NewHistoryItem
          index={index}
          length={props.data && props.data.length}
          {...props}
          historyItem={item}
          onPress={(newHistoryItem) => {
            NetInfo.fetch().then((networkState) => {
              if (!networkState.isConnected) {
                NavActions.navigate(AppRoutes.ServerErrorDialog, {
                  errorTitle: Localized.Labels.error_getting_receipt,
                  errorDesc: Localized.Labels.error_server,
                });
              } else {
                // Allow navigation to proceed if connected
                AccountHistoryList.onItemPress(newHistoryItem, true);
              }
            });
          }}
        />
      </View>
    );
  };
  return (
    <View key={`${props.title}${props.indexNo}`} testID={props.testId}>
      {props.data.length > 0 ? (
        <View style={styles.sectionHeaderView}>
          <AVText
            style={[
              getHeaderDescriber().subHeaderContainerStyleDescriptor().subHeaderTextStyle,
              getDescriber()?.historyListStyleDescriptor()?.historyHeaderTitle,
            ]}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
            accessible={true}
            accessibilityLabel={`${props.title}, `}
            aria-label={`${props.title}, `}
          >
            {props.title}
          </AVText>

          <View
            style={[
              styles.itemContainer,
              Settings.isRefiveAnd365Pay() && Styles.Style.shadow,
            ]}
          >
            {props.data && (
              <FlatList
                data={props.data}
                renderItem={renderItem}
                keyExtractor={(item, index) => `${index} ${item.Id}`}
                ItemSeparatorComponent={ItemSeparator}
              />
            )}
          </View>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  itemContainer: {
    flex: 1,
    borderWidth: 1,
    backgroundColor: Styles.white,
    borderColor: Settings.isRefiveAnd365Pay()
      ? Styles.Colors.PayNew.neutralHuesBase11
      : Styles.borderColor,
    borderRadius: Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m1,
    padding: Styles.Spacing.m1,
  },
  shadowStyles: {
    backgroundColor: Styles.white,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: {width: Styles.Spacing.m1, height: Styles.Spacing.m1},
    shadowOpacity: 0.1,
    shadowRadius: Styles.Spacing.m1,
    borderBottomWidth: 0,
  },
  sectionHeaderView: {
    flex: 1,
    paddingHorizontal: Styles.Spacing.m3 + 3,
    backgroundColor: getBGColor(),
  },
  historyContainer: {
    paddingVertical: Styles.Spacing.m2,
  },
  separator: {
    height: 1.5,
    backgroundColor: Settings.isRefiveAnd365Pay()
      ? Styles.revolveBorderColor
      : Styles.borderColor,
    marginHorizontal: 10,
    width: Settings.isRefiveAnd365Pay() ? '100%' : '90%',
  },
});
export default HistorySectionView;
